import { formatISO } from 'date-fns';
import tzlookup from '@photostructure/tz-lookup';

export function toPrettyDateStr(date, withHours = false, withMilliseconds = false) {
    const month = (date.getMonth() + 1).toString().padStart(2, '0')
    const day = date.getDate().toString().padStart(2, '0')

    let timestamp = ' ' + [date.getHours(), date.getMinutes(), date.getSeconds()].map(k => k.toString().padStart(2, '0')).join(':')
    let millisecond = date.getMilliseconds().toString().padStart(3, '0')
    return `${date.getFullYear()}-${month}-${day}${withHours ? timestamp : ''}${withMilliseconds ? '.' + millisecond : ''}`
}

export function toPrettyTimeSince(date, comparedTo = null) {
    if (isNaN(date)) {
        return 'Never'
    }
    comparedTo = comparedTo ? comparedTo : new Date()
    const diff = (comparedTo - date) / 1000

    if (diff < 0) {
        return toPrettyTimeUntil(date, comparedTo)
    }

    const secondsPrHour = 60 * 60
    const secondsPrDay = 24 * secondsPrHour
    const daysPrMonth = 28.5

    if (diff < secondsPrHour) {
        return `${Math.round(diff / 60)} minutes ago`
    }
    if (diff < secondsPrDay) {
        const hours = Math.round(diff / secondsPrHour)
        return `${hours} hour${hours === 1 ? '' : 's'} ago`
    }
    const days = Math.round(diff / secondsPrDay)
    if (days === 1) {
        return 'yesterday'
    }

    if (days > 365) {
        const yrs = Math.round(days / 365)
        return `${yrs} year${yrs === 1 ? '' : 's'} ago`
    }

    if (days > 28) {
        const months = Math.round(days / daysPrMonth)
        return `${months} month${months === 1 ? '' : 's'} ago`
    }

    return `${days} day${days === 1 ? '' : 's'} ago`
}

export function toPrettyTimeUntil(date, comparedTo = null) {
    if (isNaN(date)) {
        return 'Never'
    }
    comparedTo = comparedTo ? comparedTo : new Date().getTime()
    const diff = (date - comparedTo) / 1000

    if (diff < 0) return toPrettyTimeSince(date)

    const secondsPrHour = 60 * 60
    const secondsPrDay = 24 * secondsPrHour
    const daysPrMonth = 28.5

    const minutes = Math.round(diff / 60)

    if (diff < secondsPrHour) {
        return `In ${minutes} minute${minutes === 1 ? '' : 's'}`
    }
    if (diff < secondsPrDay) {
        const hours = Math.round(diff / secondsPrHour)
        return `In ${hours} hour${hours === 1 ? '' : 's'}`
    }
    const days = Math.round(diff / secondsPrDay)
    if (days === 1) {
        return 'tomorrow'
    }

    if (days > 365) {
        const yrs = Math.round(days / 365)
        return `In ${yrs} year${yrs === 1 ? '' : 's'}`
    }

    if (days > 28) {
        const months = Math.round(days / daysPrMonth)
        return `In ${months} month${months === 1 ? '' : 's'}`
    }

    return `In ${days} day${days === 1 ? '' : 's'}`
}

export function parseStandardWplDate(date) {
    if (!date) return null;
    const [datePart, timePart] = date.split('T')
    const [year, month, day] = datePart.split('-')
    const [hour, minute, second] = timePart.split(':')
    if (!second) {
        console.log('ohh nooo', date, timePart)
    }
    return new Date(year, month - 1, day, hour, minute, second.replace('Z', ''))
}

export function toStandardWplTimestamp(date) {
    const month = (date.getUTCMonth() + 1).toString().padStart(2, '0')
    const day = date.getUTCDate().toString().padStart(2, '0')
    let timestamp = [date.getUTCHours(), date.getUTCMinutes(), date.getUTCSeconds()].map(k => k.toString().padStart(2, '0')).join(':')
    return `${date.getFullYear()}-${month}-${day}T${timestamp}Z`
}

export function getInspectionDate(dueDate, now = new Date()) {
    if (isNaN(dueDate)) {
        return null
    }

    const diffSeconds = (dueDate - now) / 1000
    const diffDays = Math.floor(diffSeconds / (60 * 60 * 24))

    if (diffDays === 0) {
        return 'due today.'
    }

    if (diffDays > 0) {
        const time = toPrettyTimeUntil(dueDate, now)
        return `due ${time.toLowerCase()}.`
    }

    const timeAgo = toPrettyTimeSince(dueDate, now)
    return `overdue ${timeAgo.toLowerCase()}.`
}

export function formatInspectionDateToYMD(input) {
  try {
    if (!input) return "";

    const date = typeof input === "string" ? new Date(input) : input;

    if (!(date instanceof Date) || isNaN(date.getTime())) {
      throw new Error("Invalid date format.");
    }

    return date.toISOString().split("T")[0];
  } catch (error) {
    console.error("formatDateToYMD error:", error.message);
    return "";
  }
}

export function dateToLocalIso(dateString) {
    const [year, month, day] = dateString.split('-').map(Number);
    const localMidnight = new Date(year, month - 1, day); // month is 0-indexed
    return formatISO(localMidnight);
}

// Wind farm time
//
// Timestamps arrive from the backend in UTC. Shown with toPrettyDateStr they come out in the
// viewer's own time zone, which is not the wind farm's when the two are in different
// countries. There is no time zone stored for a wind farm, so it is looked up from where its
// turbines stand. The zone's name is kept internal: the lookup can answer Europe/Amsterdam for
// a German offshore farm - same clock, wrong-looking name - so it is never shown to users.

// The IANA time zone of the first turbine that has a position, or null when none has.
export function windfarmTimeZone(turbines) {
    const turbine = (turbines || []).find(t => Number.isFinite(t.lat) && Number.isFinite(t.lon))
    if (!turbine) return null

    try {
        return tzlookup(turbine.lat, turbine.lon)
    } catch (e) {
        return null
    }
}

// What to call the times in a column header, and the sentence explaining it - the same
// everywhere, so users meet one wording wherever they see wind farm times.
export function windfarmTimeLabel(timeZone) {
    if (timeZone) {
        return {
            name: 'wind farm time',
            note: "Times are shown in the wind farm's local time.",
        }
    }
    return {
        name: 'UTC',
        note: "Times are shown in UTC, because the wind farm's local time zone is not known.",
    }
}

const zonedFormatters = new Map()

// Year, month, day, hour, minute and second of `date` on a clock in `timeZone`.
function zonedParts(date, timeZone) {
    let formatter = zonedFormatters.get(timeZone)
    if (!formatter) {
        formatter = new Intl.DateTimeFormat('en-US', {
            timeZone,
            hourCycle: 'h23',
            year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit', second: '2-digit',
        })
        zonedFormatters.set(timeZone, formatter)
    }

    const parts = {}
    formatter.formatToParts(date).forEach(({ type, value }) => { parts[type] = value })
    return parts
}

// Minutes `timeZone` is ahead of UTC at `date` - daylight saving included.
function utcOffsetMinutes(date, timeZone) {
    const p = zonedParts(date, timeZone)
    const wallClock = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second)
    const wholeSeconds = Math.floor(date.getTime() / 1000) * 1000
    return Math.round((wallClock - wholeSeconds) / 60000)
}

// "+02:00", "-05:00", "+05:30".
function formatOffset(minutes) {
    const sign = minutes < 0 ? '-' : '+'
    const hours = Math.floor(Math.abs(minutes) / 60)
    const rest = Math.abs(minutes) % 60
    return `${sign}${String(hours).padStart(2, '0')}:${String(rest).padStart(2, '0')}`
}

// Like toPrettyDateStr, but on a clock in `timeZone` rather than the viewer's. UTC when null.
export function toZonedDateStr(date, timeZone, withHours = false, withMilliseconds = false) {
    const p = zonedParts(date, timeZone || 'UTC')
    const time = withHours ? ` ${p.hour}:${p.minute}:${p.second}` : ''
    const millisecond = withMilliseconds ? '.' + date.getMilliseconds().toString().padStart(3, '0') : ''
    return `${p.year}-${p.month}-${p.day}${time}${millisecond}`
}

// ISO 8601 on a clock in `timeZone`, with its offset: 2024-05-01T14:00:00.123+02:00.
// Unambiguous wherever the file ends up. UTC ("Z") when null.
export function toZonedIso(date, timeZone) {
    const [day, time] = toZonedDateStr(date, timeZone, true, true).split(' ')
    const offset = timeZone ? utcOffsetMinutes(date, timeZone) : 0
    return `${day}T${time}${offset === 0 ? 'Z' : formatOffset(offset)}`
}

// Midnight of a YYYY-MM-DD day in `timeZone`, as a UTC ISO string for the backend. Falls back
// to the viewer's own midnight when the zone is unknown.
export function zonedMidnightIso(dateString, timeZone) {
    if (!timeZone) return dateToLocalIso(dateString)

    const [year, month, day] = dateString.split('-').map(Number)
    const midnightAsIfUtc = Date.UTC(year, month - 1, day)

    // The offset at midnight can differ from the offset at the guess on a daylight saving
    // day, so look it up a second time from where the first guess landed.
    let instant = midnightAsIfUtc - utcOffsetMinutes(new Date(midnightAsIfUtc), timeZone) * 60000
    instant = midnightAsIfUtc - utcOffsetMinutes(new Date(instant), timeZone) * 60000
    return new Date(instant).toISOString()
}
